import * as React from 'react'

import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  useDeliveryCalculation,
  useGenerateCapacityArray,
} from '@/hooks/useVRPData'
import { secondsToTimeString, timeStringToSeconds } from './TimeDropdown'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

// Icons for advanced components (simplified inline icons to avoid dependencies)
const Plus = ({ className }: { className?: string }) => (
  <span className={cn('inline-block', className)}>+</span>
)
const Trash2 = ({ className }: { className?: string }) => (
  <span className={cn('inline-block', className)}>🗑️</span>
)
const Check = ({ className }: { className?: string }) => (
  <span className={cn('inline-block', className)}>✓</span>
)
const X = ({ className }: { className?: string }) => (
  <span className={cn('inline-block', className)}>✕</span>
)

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-semibold file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
          className
        )}
        ref={ref}
        data-slot="input"
        {...props}
      />
    )
  }
)
Input.displayName = 'Input'

// Product-aware capacity input component for VRP system
interface CapacityInputProps {
  value?: number[] // [weight, volume, count]
  onChange?: (value: number[]) => void
  labels?: { weight: string; volume: string; count: string }
  disabled?: boolean
  className?: string
  showUnits?: boolean
}

const ProductCapacityInput = React.forwardRef<
  HTMLDivElement,
  CapacityInputProps
>(
  (
    {
      value = [0, 0, 0],
      onChange,
      labels = { weight: 'Weight (kg)', volume: 'Volume (m³)', count: 'Items' },
      disabled,
      className,
      showUnits = true,
    },
    ref
  ) => {
    const [weight, setWeight] = React.useState(value[0] || 0)
    const [volume, setVolume] = React.useState(value[1] || 0)
    const [count, setCount] = React.useState(value[2] || 0)

    React.useEffect(() => {
      if (value) {
        setWeight(value[0] || 0)
        setVolume(value[1] || 0)
        setCount(value[2] || 0)
      }
    }, [value])

    const handleChange = (index: number, val: string) => {
      const num = Math.max(0, parseFloat(val) || 0)
      const newValues = [weight, volume, count]
      newValues[index] = num

      if (index === 0) setWeight(num)
      if (index === 1) setVolume(num)
      if (index === 2) setCount(num)

      onChange?.(newValues)
    }

    const fields = [
      {
        key: 'weight',
        label: labels.weight,
        value: weight,
        step: '0.01',
        unit: 'kg',
      },
      {
        key: 'volume',
        label: labels.volume,
        value: volume,
        step: '0.01',
        unit: 'm³',
      },
      {
        key: 'count',
        label: labels.count,
        value: count,
        step: '1',
        unit: 'items',
      },
    ]

    return (
      <div
        ref={ref}
        className={cn('grid grid-cols-3 gap-4', className)}
        data-slot="capacity-input"
      >
        {fields.map((field, index) => (
          <div key={field.key} className="space-y-1">
            {/* VRP Design System: Typography - text-sm font-semibold for labels */}
            <Label
              htmlFor={`capacity-${field.key}`}
              className="text-sm font-semibold text-foreground block"
            >
              {field.label}
            </Label>
            <div className="relative">
              <Input
                id={`capacity-${field.key}`}
                type="number"
                value={field.value || ''}
                onChange={e => handleChange(index, e.target.value)}
                disabled={disabled}
                min="0"
                step={field.step}
                className="text-center pr-8" // Space for unit display
                placeholder="0"
                aria-describedby={
                  showUnits ? `capacity-${field.key}-unit` : undefined
                }
              />
              {showUnits && (
                <span
                  id={`capacity-${field.key}-unit`}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-sm font-normal text-muted-foreground pointer-events-none"
                  aria-hidden="true"
                >
                  {field.unit}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    )
  }
)
ProductCapacityInput.displayName = 'ProductCapacityInput'

// Currency input component for cost fields
interface CurrencyInputProps {
  value?: number // Dollar value for display/editing
  cents?: number // Cent value from backend (takes priority over value)
  onChange?: (cents: number) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  currency?: string
  symbol?: string
  placeholder?: string
  disabled?: boolean
  className?: string
}

const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  (
    {
      value,
      cents,
      onChange,
      onKeyDown,
      currency = 'USD',
      symbol = '$',
      placeholder,
      disabled,
      className,
    },
    ref
  ) => {
    // Use cents if provided, otherwise convert value to cents
    const displayValue =
      cents !== undefined
        ? (cents / 100).toFixed(2)
        : value !== undefined
          ? value.toFixed(2)
          : ''

    const [inputValue, setInputValue] = React.useState(displayValue)

    React.useEffect(() => {
      const newDisplayValue =
        cents !== undefined
          ? (cents / 100).toFixed(2)
          : value !== undefined
            ? value.toFixed(2)
            : ''
      setInputValue(newDisplayValue)
    }, [cents, value])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value
      setInputValue(rawValue)

      // Parse and validate input
      const cleaned = rawValue.replace(/[^\d.-]/g, '')
      const parsed = parseFloat(cleaned)

      if (!isNaN(parsed) && parsed >= 0) {
        const newCents = Math.round(parsed * 100)
        onChange?.(newCents)
      } else if (cleaned === '' || cleaned === '0') {
        onChange?.(0)
      }
    }

    return (
      <div className="relative">
        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground text-sm">
          {symbol}
        </span>
        <input
          ref={ref}
          type="text"
          inputMode="decimal"
          value={inputValue}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus
          className={cn(
            'flex h-10 w-full rounded-md border border-input bg-background pl-8 pr-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
            className
          )}
        />
      </div>
    )
  }
)
CurrencyInput.displayName = 'CurrencyInput'

// =============================================================================
// TIME INPUT COMPONENTS FOR VRP SYSTEM
// =============================================================================

// Time conversion utilities
export const secondsToReadableDuration = (
  seconds: number | undefined,
  preferredUnit: 'auto' | 'minutes' | 'hours' = 'auto'
): string => {
  if (seconds === undefined || seconds === null || isNaN(seconds)) return ''
  const absSeconds = Math.abs(seconds)
  if (
    preferredUnit === 'hours' ||
    (preferredUnit === 'auto' && absSeconds >= 3600)
  ) {
    const hours = absSeconds / 3600
    return hours === 1 ? '1 hour' : `${hours} hours`
  } else {
    const minutes = absSeconds / 60
    return minutes === 1 ? '1 minute' : `${minutes} minutes`
  }
}

export const parseDurationToSeconds = (input: string): number => {
  if (!input || typeof input !== 'string') return 0
  const trimmed = input.trim().toLowerCase()
  if (!trimmed) return 0

  const patterns = [
    { regex: /^(\d+(?:\.\d+)?)\s*(h|hr|hrs|hour|hours)$/i, multiplier: 3600 },
    {
      regex: /^(\d+(?:\.\d+)?)\s*(m|min|mins|minute|minutes)$/i,
      multiplier: 60,
    },
  ]

  for (const pattern of patterns) {
    const match = trimmed.match(pattern.regex)
    if (match) {
      const value = parseFloat(match[1])
      if (!isNaN(value) && value >= 0)
        return Math.round(value * pattern.multiplier)
    }
  }

  const numMatch = trimmed.match(/^(\d+(?:\.\d+)?)$/)
  if (numMatch) {
    const value = parseFloat(numMatch[1])
    if (!isNaN(value) && value >= 0) return Math.round(value * 60) // Default to minutes
  }
  return 0
}

// Duration input component for VRP time fields
interface DurationInputProps {
  value?: number // seconds from backend
  onChange?: (seconds: number) => void
  placeholder?: string
  disabled?: boolean
  units?: 'minutes' | 'hours' | 'auto'
  className?: string
  min?: number
  max?: number
}

const DurationInput = React.forwardRef<HTMLDivElement, DurationInputProps>(
  (
    {
      value,
      onChange,
      placeholder = 'Enter duration...',
      disabled = false,
      units = 'auto',
      className,
      min = 0,
      max = 86400, // 24 hours
    },
    ref
  ) => {
    const [inputValue, setInputValue] = React.useState('')
    const [selectedUnit, setSelectedUnit] = React.useState<'minutes' | 'hours'>(
      units === 'auto' ? 'minutes' : units
    )

    React.useEffect(() => {
      if (value === undefined || value === null) {
        setInputValue('')
        return
      }

      if (units === 'auto') {
        if (value >= 3600) {
          setSelectedUnit('hours')
          setInputValue((value / 3600).toString())
        } else {
          setSelectedUnit('minutes')
          setInputValue((value / 60).toString())
        }
      } else {
        setSelectedUnit(units)
        const divisor = units === 'hours' ? 3600 : 60
        setInputValue((value / divisor).toString())
      }
    }, [value, units])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value
      setInputValue(rawValue)

      const numValue = parseFloat(rawValue)
      if (isNaN(numValue) || numValue < 0) {
        onChange?.(0)
        return
      }

      const multiplier = selectedUnit === 'hours' ? 3600 : 60
      const seconds = Math.round(numValue * multiplier)
      const constrainedSeconds = Math.max(min, Math.min(max, seconds))
      onChange?.(constrainedSeconds)
    }

    const displayValue = value
      ? secondsToReadableDuration(value, selectedUnit)
      : ''

    return (
      <div ref={ref} className={cn('space-y-1', className)}>
        <div className="flex gap-2">
          <Input
            type="number"
            value={inputValue}
            onChange={handleInputChange}
            placeholder={placeholder}
            disabled={disabled}
            min="0"
            step={selectedUnit === 'hours' ? '0.25' : '1'}
            className="flex-1"
          />
          <div className="flex items-center px-3 text-sm text-muted-foreground bg-muted rounded-md min-w-[60px]">
            {units === 'auto' ? selectedUnit.slice(0, 3) : units.slice(0, 3)}
          </div>
        </div>
        {displayValue && (
          <div className="text-sm text-muted-foreground">
            = {displayValue} ({value} seconds)
          </div>
        )}
      </div>
    )
  }
)
DurationInput.displayName = 'DurationInput'

// Time range input component for VRP time windows
interface TimeRangeInputProps {
  value?: number[] // [startSeconds, endSeconds] from backend
  onChange?: (range: number[]) => void
  format?: '12h' | '24h'
  disabled?: boolean
  placeholder?: string
  className?: string
}

const TimeRangeInput = React.forwardRef<HTMLInputElement, TimeRangeInputProps>(
  (
    {
      value = [],
      onChange,
      format = '24h',
      disabled = false,
      placeholder = 'e.g., 8:00 AM - 6:00 PM',
      className,
    },
    ref
  ) => {
    const [inputValue, setInputValue] = React.useState('')

    React.useEffect(() => {
      if (!value || value.length !== 2) {
        setInputValue('')
        return
      }
      const start = secondsToTimeString(value[0], format)
      const end = secondsToTimeString(value[1], format)
      setInputValue(`${start} - ${end}`)
    }, [value, format])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value
      setInputValue(rawValue)

      // Parse time range from input
      const parts = rawValue.split(/\s*-\s*/)
      if (parts.length === 2) {
        const startSeconds = timeStringToSeconds(parts[0].trim())
        const endSeconds = timeStringToSeconds(parts[1].trim())

        if (startSeconds >= 0 && endSeconds > startSeconds) {
          onChange?.([startSeconds, endSeconds])
        }
      }
    }

    const displaySeconds =
      value && value.length === 2 ? ` (${value[0]}, ${value[1]} seconds)` : ''

    return (
      <div className={cn('space-y-1', className)}>
        <Input
          ref={ref}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
        />
        {displaySeconds && (
          <div className="text-sm text-muted-foreground">
            Backend: [{value![0]}, {value![1]}] seconds
          </div>
        )}
      </div>
    )
  }
)
TimeRangeInput.displayName = 'TimeRangeInput'

// Compartment Builder for VRP Capacity System - Phase 2C Enhanced
interface Compartment {
  id: string
  name: string
  productTypes: string[]
  capacity: number[] // [weight, volume, count]
  type: string // Compartment type for visual indicators
  order: number // Display order for drag-and-drop
}

// Compartment type definitions with icons and colors
const COMPARTMENT_TYPES = {
  fuel_diesel: {
    icon: '⛽',
    color:
      'bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800',
    label: 'Diesel Tank',
    description: 'Diesel fuel storage compartment',
  },
  fuel_petrol: {
    icon: '⛽',
    color:
      'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800',
    label: 'Petrol Tank',
    description: 'Petrol fuel storage compartment',
  },
  parcel_small: {
    icon: '📦',
    color:
      'bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800',
    label: 'Small Parcels',
    description: 'Small package storage area',
  },
  parcel_medium: {
    icon: '📦',
    color:
      'bg-orange-50 border-orange-200 dark:bg-orange-950/20 dark:border-orange-800',
    label: 'Medium Parcels',
    description: 'Medium package storage area',
  },
  parcel_large: {
    icon: '📦',
    color: 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800',
    label: 'Large Parcels',
    description: 'Large package storage area',
  },
  refrigerated: {
    icon: '❄️',
    color:
      'bg-cyan-50 border-cyan-200 dark:bg-cyan-950/20 dark:border-cyan-800',
    label: 'Refrigerated',
    description: 'Temperature-controlled storage',
  },
  ambient: {
    icon: '🌡️',
    color:
      'bg-gray-50 border-gray-200 dark:bg-gray-950/20 dark:border-gray-800',
    label: 'Ambient Storage',
    description: 'Room temperature storage',
  },
  fragile: {
    icon: '⚠️',
    color:
      'bg-purple-50 border-purple-200 dark:bg-purple-950/20 dark:border-purple-800',
    label: 'Fragile Items',
    description: 'Delicate item storage area',
  },
  bulk: {
    icon: '🏗️',
    color:
      'bg-stone-50 border-stone-200 dark:bg-stone-950/20 dark:border-stone-800',
    label: 'Bulk Storage',
    description: 'Large volume bulk materials',
  },
  hazmat: {
    icon: '☣️',
    color: 'bg-red-100 border-red-300 dark:bg-red-950/30 dark:border-red-700',
    label: 'Hazardous Materials',
    description: 'Special handling required materials',
  },
} as const

type CompartmentType = keyof typeof COMPARTMENT_TYPES

interface CompartmentBuilderProps {
  value?: Compartment[]
  onChange?: (value: Compartment[]) => void
  availableProducts?: any[] // Product objects from database
  className?: string
  disabled?: boolean
  maxCompartments?: number
  isLoading?: boolean
  enableDragAndDrop?: boolean
  showAdvancedFeatures?: boolean
  enableTypeIcons?: boolean
  enableCollapsibleDetails?: boolean
  enableCapacityPreview?: boolean
}

const CompartmentBuilder = React.forwardRef<
  HTMLDivElement,
  CompartmentBuilderProps
>(
  (
    {
      value = [],
      onChange,
      availableProducts = [],
      className,
      disabled = false,
      maxCompartments = 5,
      isLoading = false,
      enableDragAndDrop = true,
      showAdvancedFeatures = true,
      enableTypeIcons = true,
      enableCollapsibleDetails = true,
      enableCapacityPreview = true,
    },
    ref
  ) => {
    // State for collapsible compartments and drag-and-drop
    const [collapsedCompartments, setCollapsedCompartments] = React.useState<
      Set<string>
    >(new Set())
    const [draggedItem, setDraggedItem] = React.useState<string | null>(null)
    const [dragOverIndex, setDragOverIndex] = React.useState<number | null>(
      null
    )

    const addCompartment = () => {
      if (value.length >= maxCompartments) return

      const newCompartment: Compartment = {
        id: `comp_${Date.now()}`,
        name: `Compartment ${value.length + 1}`,
        productTypes: [],
        capacity: [0, 0, 0],
        type: 'ambient', // Default to ambient storage
        order: value.length,
      }

      onChange?.([...value, newCompartment])
    }

    const removeCompartment = (id: string) => {
      onChange?.(value.filter(comp => comp.id !== id))
    }

    // Validation state for compartments
    const [validationErrors, setValidationErrors] = React.useState<
      Record<string, string[]>
    >({})

    // Validate compartment data
    const validateCompartment = (
      compartment: Partial<Compartment>,
      allCompartments: Compartment[],
      compartmentId?: string
    ): string[] => {
      const errors: string[] = []

      // Name validation
      if (!compartment.name || compartment.name.trim().length === 0) {
        errors.push('Compartment name is required')
      } else if (compartment.name.trim().length > 50) {
        errors.push('Compartment name must be 50 characters or less')
      } else {
        // Check for duplicate names (excluding current compartment)
        const duplicateExists = allCompartments.some(
          comp =>
            comp.id !== compartmentId &&
            comp.name.toLowerCase().trim() ===
              compartment.name!.toLowerCase().trim()
        )
        if (duplicateExists) {
          errors.push('Compartment name must be unique')
        }
      }

      // Capacity validation
      if (compartment.capacity) {
        const [weight, volume, count] = compartment.capacity
        if (weight < 0 || volume < 0 || count < 0) {
          errors.push('Capacity values must be non-negative')
        }
        if (weight === 0 && volume === 0 && count === 0) {
          errors.push(
            'At least one capacity dimension must be greater than zero'
          )
        }
        // Reasonable limits validation
        if (weight > 100000)
          errors.push('Weight capacity seems unrealistic (>100,000 kg)')
        if (volume > 1000)
          errors.push('Volume capacity seems unrealistic (>1,000 m³)')
        if (count > 100000)
          errors.push('Item count capacity seems unrealistic (>100,000 items)')
      }

      // Product type validation
      if (compartment.productTypes && compartment.productTypes.length === 0) {
        errors.push('At least one compatible product type must be selected')
      }

      // Type validation
      if (
        compartment.type &&
        !Object.keys(COMPARTMENT_TYPES).includes(compartment.type)
      ) {
        errors.push('Invalid compartment type selected')
      }

      return errors
    }

    const updateCompartment = (id: string, updates: Partial<Compartment>) => {
      const currentCompartment = value.find(comp => comp.id === id)
      if (!currentCompartment) return

      const updatedCompartment = { ...currentCompartment, ...updates }
      const newValue = value.map(comp =>
        comp.id === id ? updatedCompartment : comp
      )

      // Validate the updated compartment
      const errors = validateCompartment(updatedCompartment, newValue, id)

      // Update validation errors state
      setValidationErrors(prev => ({
        ...prev,
        [id]: errors,
      }))

      // Always update the compartment data, even with validation errors
      // This allows real-time feedback while user is typing
      onChange?.(newValue)
    }

    // Drag and drop handlers
    const handleDragStart = (e: React.DragEvent, compartmentId: string) => {
      if (!enableDragAndDrop || disabled) return
      setDraggedItem(compartmentId)
      e.dataTransfer.effectAllowed = 'move'
      // Add visual feedback
      e.currentTarget.classList.add('opacity-50')
    }

    const handleDragEnd = (e: React.DragEvent) => {
      if (!enableDragAndDrop) return
      setDraggedItem(null)
      setDragOverIndex(null)
      e.currentTarget.classList.remove('opacity-50')
    }

    const handleDragOver = (e: React.DragEvent, index: number) => {
      if (!enableDragAndDrop || !draggedItem || disabled) return
      e.preventDefault()
      setDragOverIndex(index)
      e.dataTransfer.dropEffect = 'move'
    }

    const handleDrop = (e: React.DragEvent, targetIndex: number) => {
      if (!enableDragAndDrop || !draggedItem || disabled) return
      e.preventDefault()

      const draggedIndex = value.findIndex(comp => comp.id === draggedItem)
      if (draggedIndex === -1 || draggedIndex === targetIndex) return

      const newValue = [...value]
      const [draggedCompartment] = newValue.splice(draggedIndex, 1)
      newValue.splice(targetIndex, 0, draggedCompartment)

      // Update order property
      const reorderedValue = newValue.map((comp, index) => ({
        ...comp,
        order: index,
      }))
      onChange?.(reorderedValue)

      setDraggedItem(null)
      setDragOverIndex(null)
    }

    // Toggle compartment collapse state
    const toggleCompartmentCollapse = (compartmentId: string) => {
      setCollapsedCompartments(prev => {
        const newSet = new Set(prev)
        if (newSet.has(compartmentId)) {
          newSet.delete(compartmentId)
        } else {
          newSet.add(compartmentId)
        }
        return newSet
      })
    }

    // Get compartment capacity utilization for preview
    const getCapacityUtilization = (compartment: Compartment) => {
      if (!enableCapacityPreview) return null

      const totalCapacity =
        compartment.capacity?.reduce((sum, cap) => sum + cap, 0) ?? 0
      if (totalCapacity === 0)
        return { percentage: 0, status: 'empty' as const }

      // This would need actual usage data in real implementation
      const mockUsage = totalCapacity * 0.6 // 60% usage for demo
      const percentage = (mockUsage / totalCapacity) * 100

      let status: 'good' | 'warning' | 'critical' | 'empty' = 'good'
      if (percentage === 0) status = 'empty'
      else if (percentage > 90) status = 'critical'
      else if (percentage > 75) status = 'warning'

      return { percentage, status }
    }

    if (isLoading) {
      return (
        <div
          ref={ref}
          className={cn('space-y-4', className)}
          data-slot="compartment-builder-loading"
        >
          <div className="flex items-center justify-between">
            <div className="h-4 bg-muted rounded animate-pulse w-32"></div>
            <div className="h-8 bg-muted rounded animate-pulse w-24"></div>
          </div>
          <div className="h-24 bg-muted rounded animate-pulse"></div>
        </div>
      )
    }

    return (
      <div
        ref={ref}
        className={cn('space-y-4', className)}
        data-slot="compartment-builder"
      >
        {/* VRP Design System: Typography - text-sm font-semibold for labels */}
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold text-foreground">
            Vehicle Compartments
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addCompartment}
            disabled={disabled || value.length >= maxCompartments}
            className="min-h-[44px] sm:min-h-[36px]" // Mobile touch target compliance
            aria-label={`Add compartment (${value.length}/${maxCompartments})`}
          >
            + Add Compartment
          </Button>
        </div>

        {value.length === 0 ? (
          <div
            className="text-sm font-normal text-muted-foreground text-center py-8 border-2 border-dashed border-muted-foreground/20 rounded-md bg-muted/10"
            role="status"
            aria-label="No compartments defined"
          >
            No compartments defined. Click "Add Compartment" to start.
          </div>
        ) : (
          <div className="space-y-4">
            {' '}
            {/* VRP Design System: 8pt grid - 16px spacing */}
            {value.map((compartment, index) => {
              const isCollapsed =
                enableCollapsibleDetails &&
                collapsedCompartments.has(compartment.id)
              const compartmentType =
                COMPARTMENT_TYPES[compartment.type as CompartmentType] ||
                COMPARTMENT_TYPES.ambient
              const capacityUtil = getCapacityUtilization(compartment)
              const isDragOver =
                dragOverIndex === index && draggedItem !== compartment.id

              return (
                <div
                  key={compartment.id}
                  draggable={enableDragAndDrop && !disabled}
                  onDragStart={e => handleDragStart(e, compartment.id)}
                  onDragEnd={handleDragEnd}
                  onDragOver={e => handleDragOver(e, index)}
                  onDrop={e => handleDrop(e, index)}
                  className={cn(
                    'border rounded-md bg-background transition-all duration-200', // VRP Design System: Smooth transitions
                    compartmentType.color, // Dynamic color based on compartment type
                    'hover:shadow-sm', // Subtle hover effect
                    isDragOver && 'ring-2 ring-primary ring-offset-2', // Drag over indicator
                    draggedItem === compartment.id && 'opacity-50', // Dragging state
                    enableDragAndDrop &&
                      !disabled &&
                      'cursor-grab active:cursor-grabbing', // Drag cursor
                    'p-4 space-y-4' // VRP Design System: 8pt grid - 16px padding
                  )}
                  role="region"
                  aria-label={`${compartment.name} configuration`}
                >
                  {/* Enhanced compartment header with type indicator and collapse */}
                  <div className="flex items-center gap-3">
                    {enableTypeIcons && (
                      <div className="flex items-center gap-2 shrink-0">
                        <span
                          className="text-xl"
                          role="img"
                          aria-label={compartmentType.label}
                        >
                          {compartmentType.icon}
                        </span>
                        <div className="text-sm font-normal text-muted-foreground">
                          {compartmentType.label}
                        </div>
                      </div>
                    )}

                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Label
                          htmlFor={`compartment-name-${compartment.id}`}
                          className="text-sm font-semibold text-foreground"
                        >
                          Compartment Name
                        </Label>
                        {enableCapacityPreview && capacityUtil && (
                          <Badge
                            variant={
                              capacityUtil.status === 'critical'
                                ? 'destructive'
                                : capacityUtil.status === 'warning'
                                  ? 'secondary'
                                  : 'outline'
                            }
                            className="text-sm"
                          >
                            {capacityUtil.percentage.toFixed(0)}% used
                          </Badge>
                        )}
                      </div>
                      <Input
                        id={`compartment-name-${compartment.id}`}
                        value={compartment.name}
                        onChange={e =>
                          updateCompartment(compartment.id, {
                            name: e.target.value,
                          })
                        }
                        disabled={disabled}
                        placeholder={`Compartment ${index + 1}`}
                        className={cn(
                          'mt-1',
                          validationErrors[compartment.id]?.length > 0 &&
                            'border-red-500 focus:border-red-500 focus:ring-red-500'
                        )}
                        aria-invalid={
                          validationErrors[compartment.id]?.length > 0
                        }
                        aria-describedby={
                          validationErrors[compartment.id]?.length > 0
                            ? `compartment-errors-${compartment.id}`
                            : undefined
                        }
                      />
                      {/* Validation errors display */}
                      {validationErrors[compartment.id]?.length > 0 && (
                        <div
                          id={`compartment-errors-${compartment.id}`}
                          className="mt-1 space-y-1"
                          role="alert"
                          aria-live="polite"
                        >
                          {validationErrors[compartment.id].map(
                            (error, errorIndex) => (
                              <div
                                key={errorIndex}
                                className="text-sm text-red-600 dark:text-red-400 flex items-start gap-1"
                              >
                                <span className="text-red-500 mt-0.5 shrink-0">
                                  •
                                </span>
                                <span>{error}</span>
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      {enableDragAndDrop && (
                        <div
                          className="cursor-grab text-muted-foreground hover:text-foreground p-1"
                          title="Drag to reorder"
                        >
                          <span className="text-sm">⋮⋮</span>
                        </div>
                      )}
                      {enableCollapsibleDetails && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            toggleCompartmentCollapse(compartment.id)
                          }
                          disabled={disabled}
                          className="min-h-[32px] w-8 p-0 text-muted-foreground hover:text-foreground"
                          aria-label={
                            isCollapsed
                              ? `Expand ${compartment.name}`
                              : `Collapse ${compartment.name}`
                          }
                        >
                          <span
                            className={cn(
                              'transition-transform duration-200',
                              isCollapsed && 'rotate-180'
                            )}
                          >
                            ⌄
                          </span>
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCompartment(compartment.id)}
                        disabled={disabled}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 min-h-[44px] sm:min-h-[36px]"
                        aria-label={`Remove ${compartment.name}`}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>

                  {/* Collapsible compartment details */}
                  <div
                    className={cn(
                      'space-y-4 transition-all duration-300',
                      isCollapsed && 'hidden'
                    )}
                  >
                    {/* Compartment Type Selection */}
                    {showAdvancedFeatures && (
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold text-foreground">
                          Compartment Type
                        </Label>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(COMPARTMENT_TYPES).map(
                            ([typeKey, typeConfig]) => {
                              const isSelected = compartment.type === typeKey
                              return (
                                <Button
                                  key={typeKey}
                                  type="button"
                                  variant={isSelected ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() =>
                                    updateCompartment(compartment.id, {
                                      type: typeKey,
                                    })
                                  }
                                  disabled={disabled}
                                  className={cn(
                                    'text-sm font-normal min-h-[36px] px-3 flex items-center gap-2',
                                    !isSelected && 'hover:bg-muted/50'
                                  )}
                                  title={typeConfig.description}
                                >
                                  <span className="text-lg">
                                    {typeConfig.icon}
                                  </span>
                                  <span className="hidden sm:inline">
                                    {typeConfig.label}
                                  </span>
                                </Button>
                              )
                            }
                          )}
                        </div>
                      </div>
                    )}

                    {/* Available Products Selection */}
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-foreground">
                        Compatible Products
                      </Label>
                      {availableProducts.length === 0 ? (
                        <div className="text-sm font-normal text-muted-foreground py-2">
                          No products available. Create products in the project
                          to assign them to compartments.
                        </div>
                      ) : (
                        <div
                          className={cn(
                            'max-h-32 overflow-y-auto space-y-1 border rounded-md p-2 bg-muted/10',
                            validationErrors[compartment.id]?.some(
                              error =>
                                error.includes('compatible product') ||
                                error.includes('product type')
                            ) && 'border-red-500'
                          )}
                        >
                          {availableProducts.map(product => {
                            const isSelected =
                              compartment.productTypes?.includes(product._id) ||
                              false
                            return (
                              <div
                                key={product._id}
                                className={`flex items-center justify-between p-2 rounded-md cursor-pointer transition-colors ${
                                  isSelected
                                    ? 'bg-primary/10 border border-primary/20'
                                    : 'bg-background hover:bg-muted/50 border border-transparent'
                                }`}
                                onClick={() => {
                                  const updatedTypes = isSelected
                                    ? (compartment.productTypes || []).filter(
                                        id => id !== product._id
                                      )
                                    : [
                                        ...(compartment.productTypes || []),
                                        product._id,
                                      ]
                                  updateCompartment(compartment.id, {
                                    productTypes: updatedTypes,
                                  })
                                }}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-sm truncate">
                                    {product.name}
                                  </div>
                                  <div className="text-sm text-muted-foreground mt-1">
                                    {product.category && (
                                      <span className="mr-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-sm">
                                        {product.category}
                                      </span>
                                    )}
                                    <span>{product.unitType}</span>
                                    {product.weightPerUnitKg && (
                                      <span className="ml-2">
                                        {product.weightPerUnitKg}kg/unit
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="ml-2">
                                  {isSelected ? (
                                    <div className="w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                                      <div className="w-2 h-2 bg-white rounded-full" />
                                    </div>
                                  ) : (
                                    <div className="w-4 h-4 border-2 border-muted-foreground rounded-full" />
                                  )}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                      {/* Product types validation errors display */}
                      {validationErrors[compartment.id]?.filter(
                        error =>
                          error.includes('compatible product') ||
                          error.includes('product type')
                      ).length > 0 && (
                        <div
                          className="mt-1 space-y-1"
                          role="alert"
                          aria-live="polite"
                        >
                          {validationErrors[compartment.id]
                            .filter(
                              error =>
                                error.includes('compatible product') ||
                                error.includes('product type')
                            )
                            .map((error, errorIndex) => (
                              <div
                                key={errorIndex}
                                className="text-sm text-red-600 dark:text-red-400 flex items-start gap-1"
                              >
                                <span className="text-red-500 mt-0.5 shrink-0">
                                  •
                                </span>
                                <span>{error}</span>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>

                    {/* Capacity Configuration */}
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-foreground">
                        Capacity Limits
                      </Label>
                      <ProductCapacityInput
                        value={compartment.capacity}
                        onChange={capacity =>
                          updateCompartment(compartment.id, { capacity })
                        }
                        disabled={disabled}
                        className={cn(
                          validationErrors[compartment.id]?.some(
                            error =>
                              error.includes('capacity') ||
                              error.includes('Weight') ||
                              error.includes('Volume') ||
                              error.includes('Item count') ||
                              error.includes('dimension')
                          ) && 'border-red-500'
                        )}
                      />
                      {/* Capacity validation errors display */}
                      {validationErrors[compartment.id]?.filter(
                        error =>
                          error.includes('capacity') ||
                          error.includes('Weight') ||
                          error.includes('Volume') ||
                          error.includes('Item count') ||
                          error.includes('dimension')
                      ).length > 0 && (
                        <div
                          className="mt-1 space-y-1"
                          role="alert"
                          aria-live="polite"
                        >
                          {validationErrors[compartment.id]
                            .filter(
                              error =>
                                error.includes('capacity') ||
                                error.includes('Weight') ||
                                error.includes('Volume') ||
                                error.includes('Item count') ||
                                error.includes('dimension')
                            )
                            .map((error, errorIndex) => (
                              <div
                                key={errorIndex}
                                className="text-sm text-red-600 dark:text-red-400 flex items-start gap-1"
                              >
                                <span className="text-red-500 mt-0.5 shrink-0">
                                  •
                                </span>
                                <span>{error}</span>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }
)
CompartmentBuilder.displayName = 'CompartmentBuilder'

// Advanced Capacity Utilization Bar with Analytics - Phase 2C Task 2
interface CapacityData {
  current: number
  capacity: number
  historical?: number[] // Historical usage data points
  predicted?: number // Predicted future usage
  trend?: 'increasing' | 'decreasing' | 'stable'
}

interface CapacityAnalytics {
  efficiency: number // 0-100, optimal utilization score
  wasteRisk: 'low' | 'medium' | 'high' // Under-utilization risk
  overloadRisk: 'low' | 'medium' | 'high' // Over-capacity risk
  recommendations: string[] // Optimization suggestions
}

interface CapacityUtilizationBarProps {
  used: number[] // [weight, volume, count] used
  capacity: number[] // [weight, volume, count] capacity
  labels?: { weight: string; volume: string; count: string }
  showPercentages?: boolean
  showValues?: boolean
  className?: string
  size?: 'sm' | 'md' | 'lg'
  isLoading?: boolean
  // Phase 2C Advanced Analytics Features
  enableAdvancedAnalytics?: boolean
  showPredictiveWarnings?: boolean
  showHistoricalTrends?: boolean
  showOptimizationSuggestions?: boolean
  historicalData?: {
    weight?: number[]
    volume?: number[]
    count?: number[]
  }
  predictedUsage?: {
    weight?: number
    volume?: number
    count?: number
  }
  routeData?: {
    totalStops: number
    remainingStops: number
    estimatedCompletion: number // hours
  }
}

const CapacityUtilizationBar = React.forwardRef<
  HTMLDivElement,
  CapacityUtilizationBarProps
>(
  (
    {
      used = [0, 0, 0],
      capacity = [0, 0, 0],
      labels = { weight: 'Weight', volume: 'Volume', count: 'Count' },
      showPercentages = true,
      showValues = true,
      className,
      size = 'md',
      isLoading = false,
      enableAdvancedAnalytics = false,
      showPredictiveWarnings = false,
      showHistoricalTrends = false,
      showOptimizationSuggestions = false,
      historicalData,
      predictedUsage,
      routeData,
    },
    ref
  ) => {
    // Enhanced utilization calculation with analytics
    const utilization = React.useMemo(() => {
      return capacity.map((cap, index) => {
        if (cap === 0)
          return { percentage: 0, status: 'empty' as const, analytics: null }

        const pct = Math.min(100, (used[index] / cap) * 100)
        const dimensionKey =
          index === 0 ? 'weight' : index === 1 ? 'volume' : 'count'

        // Basic status calculation
        let status: 'good' | 'warning' | 'critical' | 'empty' = 'good'
        if (pct === 0) status = 'empty'
        else if (pct > 100) status = 'critical'
        else if (pct > 80) status = 'warning'

        // Advanced analytics calculation
        let analytics: CapacityAnalytics | null = null
        if (enableAdvancedAnalytics) {
          const historical =
            historicalData?.[dimensionKey as keyof typeof historicalData] || []
          const predicted =
            predictedUsage?.[dimensionKey as keyof typeof predictedUsage]

          // Calculate efficiency (optimal range: 70-90%)
          let efficiency = 100
          if (pct < 50)
            efficiency = Math.max(0, pct * 1.4) // Under-utilization penalty
          else if (pct > 90)
            efficiency = Math.max(0, 100 - (pct - 90) * 3) // Over-utilization penalty
          else efficiency = Math.min(100, pct * 1.2) // Optimal range bonus

          // Risk assessment
          const wasteRisk = pct < 30 ? 'high' : pct < 60 ? 'medium' : 'low'
          const overloadRisk = pct > 95 ? 'high' : pct > 80 ? 'medium' : 'low'

          // Generate recommendations
          const recommendations: string[] = []
          if (pct > 95)
            recommendations.push('Consider larger capacity or route splitting')
          if (pct < 30)
            recommendations.push('Opportunity for route consolidation')
          if (predicted && predicted > cap * 0.9)
            recommendations.push(
              'Predicted capacity exceeded - plan redistribution'
            )
          if (historical.length > 0) {
            const trend =
              historical[historical.length - 1] > historical[0]
                ? 'increasing'
                : 'decreasing'
            if (trend === 'increasing' && pct > 70) {
              recommendations.push(
                'Increasing trend detected - monitor closely'
              )
            }
          }

          analytics = { efficiency, wasteRisk, overloadRisk, recommendations }
        }

        return {
          percentage: pct,
          status,
          analytics,
          predicted,
          historical:
            historicalData?.[dimensionKey as keyof typeof historicalData],
        }
      })
    }, [
      used,
      capacity,
      enableAdvancedAnalytics,
      historicalData,
      predictedUsage,
    ])

    // Calculate predictive warnings
    const predictiveWarnings = React.useMemo(() => {
      if (!showPredictiveWarnings || !routeData) return []

      const warnings: string[] = []
      const remainingRatio = routeData.remainingStops / routeData.totalStops

      utilization.forEach((util, index) => {
        const dimensionName =
          index === 0 ? 'weight' : index === 1 ? 'volume' : 'count'

        // Predict end-of-route capacity if current trend continues
        const projectedUsage = used[index] + used[index] * remainingRatio * 0.8 // 80% of current rate
        const projectedPercent = (projectedUsage / capacity[index]) * 100

        if (projectedPercent > 100) {
          warnings.push(
            `${dimensionName} may exceed capacity before route completion`
          )
        } else if (projectedPercent > 90) {
          warnings.push(`${dimensionName} approaching capacity limits`)
        }
      })

      return warnings
    }, [utilization, used, capacity, routeData, showPredictiveWarnings])

    // VRP Design System: 8pt grid system compliance - 4px, 8px, 12px heights
    const sizeClasses = {
      sm: 'h-1', // 4px
      md: 'h-2', // 8px
      lg: 'h-3', // 12px
    }

    // VRP Design System: Consistent color scheme using design tokens
    const getStatusColor = (status: string) => {
      switch (status) {
        case 'good':
          return 'bg-emerald-500 dark:bg-emerald-400' // High contrast accessibility
        case 'warning':
          return 'bg-amber-500 dark:bg-amber-400' // Better than yellow for accessibility
        case 'critical':
          return 'bg-red-500 dark:bg-red-400' // Consistent with destructive theme
        default:
          return 'bg-muted' // Design system token
      }
    }

    // VRP Design System: Consistent text color variants
    const getStatusTextColor = (status: string) => {
      switch (status) {
        case 'good':
          return 'text-emerald-600 dark:text-emerald-400'
        case 'warning':
          return 'text-amber-600 dark:text-amber-400'
        case 'critical':
          return 'text-red-600 dark:text-red-400'
        default:
          return 'text-muted-foreground'
      }
    }

    if (isLoading) {
      return (
        <div
          ref={ref}
          className={cn('space-y-4', className)}
          data-slot="capacity-utilization-loading"
        >
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="h-3 bg-muted rounded animate-pulse w-16"></div>
                <div className="h-3 bg-muted rounded animate-pulse w-20"></div>
              </div>
              <div
                className={cn(
                  'w-full bg-muted rounded-full animate-pulse',
                  sizeClasses[size]
                )}
              ></div>
            </div>
          ))}
        </div>
      )
    }

    return (
      <div
        ref={ref}
        className={cn('space-y-4', className)}
        data-slot="capacity-utilization"
      >
        {used.map((usedValue, index) => {
          const capValue = capacity[index]
          const util = utilization[index]
          const labelKey =
            index === 0 ? 'weight' : index === 1 ? 'volume' : 'count'
          const label = labels[labelKey as keyof typeof labels]

          return (
            <div key={index} className="space-y-2">
              {/* VRP Design System: Typography - Size 4 for labels, font-semibold for emphasis */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-foreground">
                  {label}
                </span>
                <div className="flex items-center gap-2">
                  {showValues && (
                    <span className="text-sm font-normal text-muted-foreground">
                      {usedValue.toFixed(index === 2 ? 0 : 2)} /{' '}
                      {capValue.toFixed(index === 2 ? 0 : 2)}
                      {index === 0 ? 'kg' : index === 1 ? 'm³' : ' items'}
                    </span>
                  )}
                  {showPercentages && (
                    <span
                      className={cn(
                        'text-sm font-semibold',
                        getStatusTextColor(util.status)
                      )}
                      aria-label={`${util.percentage.toFixed(0)}% capacity used`}
                    >
                      {util.percentage.toFixed(0)}%
                    </span>
                  )}
                </div>
              </div>

              {/* Progress bar with proper accessibility */}
              <div
                className={cn(
                  'w-full bg-muted rounded-full overflow-hidden',
                  sizeClasses[size]
                )}
                role="progressbar"
                aria-valuenow={util.percentage}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${label} utilization: ${util.percentage.toFixed(0)}%`}
              >
                <div
                  className={cn(
                    'transition-all duration-300 rounded-full',
                    getStatusColor(util.status)
                  )}
                  style={{ width: `${Math.min(100, util.percentage)}%` }}
                />
              </div>

              {/* Over capacity warning with proper styling */}
              {util.percentage > 100 && (
                <div
                  className="text-sm font-semibold text-red-600 dark:text-red-400 flex items-center gap-1"
                  role="alert"
                  aria-live="polite"
                >
                  <span className="text-red-500" aria-hidden="true">
                    ⚠️
                  </span>
                  Over capacity by {(util.percentage - 100).toFixed(0)}%
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }
)
CapacityUtilizationBar.displayName = 'CapacityUtilizationBar'

// Delivery Items Manager for product-based job requirements
interface DeliveryItem {
  productId: string
  quantity: number
  unit: string
}

interface DeliveryItemsManagerProps {
  value?: DeliveryItem[]
  onChange?: (items: DeliveryItem[]) => void
  projectId: string
  products?: any[]
  mode?: 'delivery' | 'pickup'
  className?: string
  disabled?: boolean
  showCapacityValidation?: boolean
  selectedVehicleId?: string
  jobId?: string // Added for auto-calculation integration
}

const DeliveryItemsManager = React.forwardRef<
  HTMLDivElement,
  DeliveryItemsManagerProps
>(
  (
    {
      value = [],
      onChange,
      projectId,
      products = [],
      mode = 'delivery',
      className,
      disabled = false,
      showCapacityValidation = false,
      selectedVehicleId,
      jobId,
    },
    ref
  ) => {
    const [items, setItems] = React.useState<DeliveryItem[]>(value)
    const [isAddingItem, setIsAddingItem] = React.useState(false)
    const [selectedProductId, setSelectedProductId] = React.useState<string>('')
    const [selectedQuantity, setSelectedQuantity] = React.useState<number>(1)

    // Local state for tracking quantity editing values (prevents premature auto-save)
    const [editingQuantities, setEditingQuantities] = React.useState<{
      [index: number]: string
    }>({})

    // Utility function for debouncing quantity updates
    const debounce = React.useCallback((func: Function, wait: number) => {
      let timeout: NodeJS.Timeout
      return (...args: any[]) => {
        clearTimeout(timeout)
        timeout = setTimeout(() => func(...args), wait)
      }
    }, [])

    // Enhanced auto-calculation hook with performance monitoring
    const {
      calculating,
      lastError,
      triggerCalculation,
      getArrayInfo,
      clearError,
    } = useDeliveryCalculation(
      jobId as any, // Type conversion for compatibility
      projectId as any
    )

    // Get current array info for performance display
    const currentArrayInfo = React.useMemo(() => {
      if (!items || items.length === 0) return null
      return getArrayInfo(items)
    }, [items, getArrayInfo])

    // Adaptive debouncing based on project size and array complexity
    const getAdaptiveDebounceTime = React.useCallback(() => {
      const arraySize = currentArrayInfo?.arraySize || 0
      if (arraySize > 150) return 3000 // 3s for very large arrays
      if (arraySize > 75) return 2000 // 2s for large arrays
      if (arraySize > 30) return 1500 // 1.5s for medium arrays
      return 1000 // 1s for small arrays
    }, [currentArrayInfo])

    // Enhanced debounced calculation with adaptive timing and performance monitoring
    const debouncedCalculation = React.useCallback(
      debounce(async (items: DeliveryItem[]) => {
        try {
          const arrayInfo = getArrayInfo(items)

          console.log('🧮 Enhanced delivery calculation starting:', {
            activeItems: items.length,
            totalProducts: arrayInfo.totalProducts,
            estimatedArraySize: arrayInfo.arraySize,
            compressionRatio: arrayInfo.compressionRatio,
            isLargeArray: arrayInfo.isLargeArray,
            debounceTime: getAdaptiveDebounceTime(),
          })

          const result = await triggerCalculation(items)
          if (result.success && result.deliveryArray) {
            console.log('✅ Enhanced calculation successful')
          }
        } catch (error) {
          console.error('❌ Enhanced calculation error:', error)
        }
      }, getAdaptiveDebounceTime()),
      [triggerCalculation, getArrayInfo, getAdaptiveDebounceTime]
    )

    // Use products from database only - no fallback
    const availableProducts = React.useMemo(() => {
      if (products && products.length > 0) {
        return products.map(product => ({
          id: product._id,
          name: product.name,
          unitType: product.unitType || 'items',
        }))
      }
      // Return empty array if no products - user needs to create products first
      return []
    }, [products])

    React.useEffect(() => {
      setItems(value)
    }, [value])

    // Sanitize delivery item to only include schema-compliant fields
    const sanitizeDeliveryItem = (item: any): DeliveryItem => ({
      productId: item.productId,
      quantity: item.quantity,
      unit: item.unit,
    })

    const handleItemsChange = (updatedItems: DeliveryItem[]) => {
      console.log('🔄 DeliveryItemsManager: handleItemsChange called with:', {
        updatedItems,
        mode,
        projectId,
        itemCount: updatedItems.length,
        timestamp: new Date().toISOString(),
      })
      setItems(updatedItems)
      if (onChange) {
        // Sanitize items before sending to backend to ensure schema compliance
        const cleanItems = updatedItems.map(sanitizeDeliveryItem)
        console.log(
          '🚀 DeliveryItemsManager: Calling onChange callback with clean items:',
          cleanItems
        )
        onChange(cleanItems)
      } else {
        console.warn('⚠️ DeliveryItemsManager: No onChange callback provided')
      }

      // Trigger auto-calculation for delivery mode only (pickup calculations handled separately)
      if (mode === 'delivery' && jobId && updatedItems.length > 0) {
        debouncedCalculation(updatedItems.map(sanitizeDeliveryItem))
      }
    }

    const addItem = () => {
      setIsAddingItem(true)
      // Reset form when opening
      setSelectedProductId('')
      setSelectedQuantity(1)
    }

    const resetForm = () => {
      setSelectedProductId('')
      setSelectedQuantity(1)
      setIsAddingItem(false)
    }

    const isFormValid = selectedProductId && selectedQuantity > 0

    const confirmAddItem = () => {
      // Validate form before proceeding
      if (!isFormValid) {
        console.warn('⚠️ DeliveryItemsManager: Form validation failed:', {
          selectedProductId,
          selectedQuantity,
          isFormValid,
        })
        return
      }

      console.log(
        '➕ DeliveryItemsManager: confirmAddItem called with form data:',
        {
          productId: selectedProductId,
          quantity: selectedQuantity,
          mode,
          currentItemCount: items.length,
        }
      )

      const product = availableProducts.find(p => p.id === selectedProductId)
      if (!product) {
        console.error(
          '❌ DeliveryItemsManager: Product not found:',
          selectedProductId
        )
        return
      }

      const newItem: DeliveryItem = {
        productId: selectedProductId,
        quantity: selectedQuantity,
        unit: product.unitType || 'items',
      }

      console.log('✅ DeliveryItemsManager: Adding new item:', newItem)
      handleItemsChange([...items, newItem])
      resetForm()
    }

    const removeItem = (index: number) => {
      handleItemsChange(items.filter((_, idx) => idx !== index))
    }

    const updateItem = (index: number, updates: Partial<DeliveryItem>) => {
      handleItemsChange(
        items.map((item, idx) =>
          idx === index ? { ...item, ...updates } : item
        )
      )
    }

    // Debounced quantity update function to prevent premature auto-save
    const debouncedQuantityUpdate = React.useMemo(
      () =>
        debounce((index: number, quantity: number) => {
          console.log('🔄 Debounced quantity update:', { index, quantity })
          updateItem(index, { quantity })
        }, 500), // 500ms delay allows user to finish typing
      [debounce]
    )

    // Calculate total capacity usage
    const totalCapacityUsage = React.useMemo(() => {
      return items.reduce(
        (total, item) => {
          // This would need proper product data to calculate actual weight/volume
          const estimatedWeight = item.quantity * 1 // 1kg per item assumption
          const estimatedVolume = item.quantity * 0.1 // 0.1m³ per item assumption
          return [
            total[0] + estimatedWeight,
            total[1] + estimatedVolume,
            total[2] + item.quantity,
          ]
        },
        [0, 0, 0]
      )
    }, [items])

    return (
      <div ref={ref} className={cn('space-y-3', className)}>
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">
            {mode === 'delivery' ? 'Items to Deliver' : 'Items to Pickup'}
          </Label>
          <div className="flex items-center gap-2">
            {/* Enhanced performance-aware calculation status indicators */}
            {mode === 'delivery' && jobId && calculating && (
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <span className="animate-pulse">🧮</span>
                <span>
                  Calculating...
                  {currentArrayInfo && (
                    <span className="ml-1 text-muted-foreground/70">
                      ({currentArrayInfo.activeProducts} of{' '}
                      {currentArrayInfo.totalProducts} products)
                    </span>
                  )}
                </span>
                {currentArrayInfo?.isLargeArray && (
                  <span
                    className="text-orange-500"
                    title="Large array - may take longer"
                  >
                    ⚡
                  </span>
                )}
              </div>
            )}

            {/* Performance information display */}
            {mode === 'delivery' &&
              jobId &&
              !calculating &&
              currentArrayInfo && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <span>📊</span>
                  <span>
                    {currentArrayInfo.activeProducts}/
                    {currentArrayInfo.totalProducts} products
                    {currentArrayInfo.isLargeArray && (
                      <span
                        className="text-orange-600 ml-1"
                        title="Large sparse array detected"
                      >
                        ({currentArrayInfo.arraySize}-element array)
                      </span>
                    )}
                  </span>
                </div>
              )}

            {mode === 'delivery' && jobId && lastError && (
              <div className="flex items-center gap-1 text-sm text-red-600">
                <span>⚠️</span>
                <button
                  onClick={clearError}
                  className="hover:underline"
                  title={lastError}
                >
                  Calculation failed
                </button>
              </div>
            )}
            <Button
              onClick={addItem}
              disabled={disabled || availableProducts.length === 0}
              size="sm"
              variant="outline"
              className="h-8"
              title={
                availableProducts.length === 0
                  ? 'No products available. Create products in the project first.'
                  : undefined
              }
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Item
            </Button>
          </div>
        </div>

        {/* Items list */}
        {items.length > 0 && (
          <div className="space-y-2">
            {items.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-2 p-2 border rounded-md bg-muted/30"
              >
                <div className="flex-1">
                  <div className="text-sm font-semibold">
                    {availableProducts.find(p => p.id === item.productId)
                      ?.name || item.productId}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {item.quantity} {item.unit}
                  </div>
                </div>
                <Input
                  type="number"
                  value={editingQuantities[index] ?? item.quantity}
                  onChange={e => {
                    const newValue = e.target.value
                    // Update local editing state immediately (no auto-save)
                    setEditingQuantities(prev => ({
                      ...prev,
                      [index]: newValue,
                    }))

                    // Debounce the actual update (triggers auto-save after 500ms)
                    const numValue = parseInt(newValue) || 0
                    if (numValue > 0) {
                      debouncedQuantityUpdate(index, numValue)
                    }
                  }}
                  onBlur={e => {
                    // Immediate save on blur (when user clicks away)
                    const finalValue = parseInt(e.target.value) || 1
                    updateItem(index, { quantity: finalValue })
                    // Clear editing state
                    setEditingQuantities(prev => {
                      const { [index]: removed, ...rest } = prev
                      return rest
                    })
                  }}
                  min="1"
                  className="w-20 h-8"
                  disabled={disabled}
                />
                <Button
                  onClick={() => removeItem(index)}
                  disabled={disabled}
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Add item form */}
        {isAddingItem && (
          <div className="p-3 border rounded-md bg-muted/50 space-y-3">
            <Label className="text-sm font-semibold">Add New Item</Label>
            <div className="grid grid-cols-3 gap-2">
              <select
                className="h-8 px-2 border rounded text-sm bg-background"
                value={selectedProductId}
                onChange={e => setSelectedProductId(e.target.value)}
                disabled={disabled}
              >
                <option value="">Select Product</option>
                {availableProducts.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
              <Input
                type="number"
                placeholder="Quantity"
                min="1"
                value={selectedQuantity}
                onChange={e =>
                  setSelectedQuantity(parseInt(e.target.value) || 1)
                }
                className="h-8"
                disabled={disabled}
              />
              <div className="flex gap-1">
                <Button
                  onClick={confirmAddItem}
                  size="sm"
                  className="h-8 flex-1"
                  disabled={disabled || !isFormValid}
                >
                  <Check className="w-4 h-4" />
                </Button>
                <Button
                  onClick={resetForm}
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  disabled={disabled}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Capacity summary */}
        {showCapacityValidation && items.length > 0 && (
          <div className="p-2 bg-muted/30 rounded text-sm">
            <div className="font-semibold text-muted-foreground mb-1">
              Estimated Capacity Usage:
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <span>{totalCapacityUsage[0]}kg weight</span>
              <span>{totalCapacityUsage[1]}m³ volume</span>
              <span>{totalCapacityUsage[2]} items</span>
            </div>
          </div>
        )}

        {/* Empty state */}
        {items.length === 0 && !isAddingItem && (
          <div className="text-center py-4 text-sm text-muted-foreground">
            {availableProducts.length === 0 ? (
              <>
                No products available in this project.
                <br />
                Create products first to specify{' '}
                {mode === 'delivery' ? 'delivery' : 'pickup'} items.
              </>
            ) : (
              <>
                No {mode === 'delivery' ? 'delivery' : 'pickup'} items
                specified.
                <br />
                Click "Add Item" to specify products.
              </>
            )}
          </div>
        )}
      </div>
    )
  }
)
DeliveryItemsManager.displayName = 'DeliveryItemsManager'

// =============================================================================
// PHASE 3: COMPARTMENT ITEMS MANAGER - CAPACITY SYSTEM FRONTEND
// =============================================================================

/**
 * Calculate arrayIndices for a product based on its position in the sorted products list.
 * This matches the backend algorithm in jobs.ts:2516-2617
 *
 * @param productId - The product ID to calculate indices for
 * @param products - Array of all project products
 * @returns [weightIndex, volumeIndex, countIndex]
 * @throws Error if products unavailable or product not found
 */
const calculateArrayIndicesForProduct = (
  productId: string,
  products: any[]
): [number, number, number] => {
  // Validate products array
  if (!products || products.length === 0) {
    throw new Error('Products list is empty - cannot calculate arrayIndices')
  }

  // Sort products by _creationTime (same as backend algorithm)
  const sortedProducts = [...products].sort(
    (a, b) => (a._creationTime || 0) - (b._creationTime || 0)
  )

  // Find product index in sorted list
  const productIndex = sortedProducts.findIndex(p => p._id === productId)

  if (productIndex === -1) {
    throw new Error(
      `Product ${productId} not found in sorted products list. ` +
        `Cannot calculate arrayIndices for compartment.`
    )
  }

  // Calculate sparse array indices: [weight, volume, count]
  return [
    productIndex * 3, // Weight index
    productIndex * 3 + 1, // Volume index
    productIndex * 3 + 2, // Count index
  ]
}

// Types for compartment management
interface CompartmentItem {
  id: string
  name: string
  productId: string // ✅ Reference to actual product (was productType)
  capacityValue: number
  capacityUnit: string
  arrayIndices: number[] // ✅ Required by schema - maps to capacity array positions [weight, volume, count]
}

interface CompartmentItemsManagerProps {
  value?: CompartmentItem[]
  onChange?: (items: CompartmentItem[]) => void
  projectId: string
  products?: any[]
  className?: string
  disabled?: boolean
  vehicleId?: string // For auto-calculation integration
  showCapacityValidation?: boolean
  enablePerformanceMonitoring?: boolean
}

const CompartmentItemsManager = React.forwardRef<
  HTMLDivElement,
  CompartmentItemsManagerProps
>(
  (
    {
      value = [],
      onChange,
      projectId,
      products = [],
      className,
      disabled = false,
      vehicleId,
      showCapacityValidation = false,
      enablePerformanceMonitoring = false,
    },
    ref
  ) => {
    const [items, setItems] = React.useState<CompartmentItem[]>(value)
    const [isAddingItem, setIsAddingItem] = React.useState(false)
    const [selectedProductId, setSelectedProductId] = React.useState<string>('')
    const [selectedCapacityValue, setSelectedCapacityValue] =
      React.useState<number>(1000)
    const [selectedCapacityUnit, setSelectedCapacityUnit] =
      React.useState<string>('liters')

    // Local state for tracking capacity editing values
    const [editingCapacities, setEditingCapacities] = React.useState<{
      [index: number]: string
    }>({})

    // Utility function for debouncing capacity updates
    const debounce = React.useCallback((func: Function, wait: number) => {
      let timeout: NodeJS.Timeout
      return (...args: any[]) => {
        clearTimeout(timeout)
        timeout = setTimeout(() => func(...args), wait)
      }
    }, [])

    // Available products from products table (replacing hardcoded types)
    const availableProducts = React.useMemo(() => {
      return products?.filter(p => p._id && p.name) || []
    }, [products])

    // Available units based on selected product
    const getUnitsForProduct = React.useCallback(
      (productId: string) => {
        const product = products?.find(p => p._id === productId)
        if (!product) return ['liters', 'kg', 'items'] // Default units

        // Use product's unitType if available, otherwise infer from category
        if (product.unitType) {
          return [product.unitType]
        }

        // Fallback inference from category/name
        const productName = product.name?.toLowerCase() || ''
        const category = product.category?.toLowerCase() || ''

        if (productName.includes('fuel') || category.includes('fuel')) {
          return ['liters', 'cubic_meters']
        }
        if (
          productName.includes('parcel') ||
          category.includes('parcel') ||
          category.includes('package')
        ) {
          return ['items', 'pieces', 'pallets', 'kg', 'tons']
        }
        return ['kg', 'tons', 'liters', 'cubic_meters', 'items', 'pieces']
      },
      [products]
    )

    React.useEffect(() => {
      setItems(value)
    }, [value])

    // Sanitize compartment item to only include schema-compliant fields
    const sanitizeCompartmentItem = (item: any): CompartmentItem => {
      // Calculate arrayIndices if missing - DO NOT use hardcoded fallback
      let arrayIndices = item.arrayIndices
      if (!arrayIndices) {
        try {
          arrayIndices = calculateArrayIndicesForProduct(
            item.productId,
            products
          )
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Unknown error calculating arrayIndices'
          console.error(
            'Failed to calculate arrayIndices during sanitization:',
            errorMessage
          )
          throw new Error(`Cannot sanitize compartment item: ${errorMessage}`)
        }
      }

      return {
        id: item.id,
        name: item.name,
        productId: item.productId,
        capacityValue: item.capacityValue,
        capacityUnit: item.capacityUnit,
        arrayIndices: arrayIndices,
      }
    }

    const handleItemsChange = (updatedItems: CompartmentItem[]) => {
      console.log(
        '🔄 CompartmentItemsManager: handleItemsChange called with:',
        {
          updatedItems,
          projectId,
          vehicleId,
          itemCount: updatedItems.length,
          timestamp: new Date().toISOString(),
        }
      )
      setItems(updatedItems)
      if (onChange) {
        // Sanitize items before sending to backend to ensure schema compliance
        const cleanItems = updatedItems.map(sanitizeCompartmentItem)
        console.log(
          '🚀 CompartmentItemsManager: Calling onChange callback with clean items:',
          cleanItems
        )
        onChange(cleanItems)
      } else {
        console.warn(
          '⚠️ CompartmentItemsManager: No onChange callback provided'
        )
      }
    }

    const addItem = () => {
      setIsAddingItem(true)
      // Reset form when opening
      setSelectedProductId('')
      setSelectedCapacityValue(1000)
      setSelectedCapacityUnit('liters')
    }

    const resetForm = () => {
      setSelectedProductId('')
      setSelectedCapacityValue(1000)
      setSelectedCapacityUnit('liters')
      setIsAddingItem(false)
    }

    const isFormValid = selectedProductId && selectedCapacityValue > 0

    const confirmAddItem = () => {
      // Validate form before proceeding
      if (!isFormValid) {
        console.warn('⚠️ CompartmentItemsManager: Form validation failed:', {
          selectedProductId,
          selectedCapacityValue,
          isFormValid,
        })
        return
      }

      console.log(
        '➕ CompartmentItemsManager: confirmAddItem called with form data:',
        {
          productId: selectedProductId,
          capacityValue: selectedCapacityValue,
          capacityUnit: selectedCapacityUnit,
          currentItemCount: items.length,
        }
      )

      // Get selected product for name generation
      const selectedProduct = products?.find(p => p._id === selectedProductId)
      const productName = selectedProduct?.name || 'Unknown Product'

      // Calculate arrayIndices based on product position (matches backend algorithm)
      let arrayIndices: [number, number, number]
      try {
        arrayIndices = calculateArrayIndicesForProduct(
          selectedProductId,
          products
        )
      } catch (error) {
        // Display user-friendly error - DO NOT create compartment with wrong indices
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Unknown error calculating arrayIndices'
        console.error('Failed to calculate arrayIndices:', errorMessage)
        alert(`Cannot create compartment: ${errorMessage}`)
        return // Abort compartment creation
      }

      const newItem: CompartmentItem = {
        id: `compartment-${Date.now()}`,
        name: `${productName} Compartment`,
        productId: selectedProductId,
        capacityValue: selectedCapacityValue,
        capacityUnit: selectedCapacityUnit,
        arrayIndices: arrayIndices, // ✅ CORRECT - calculated from product position
      }

      console.log('✅ CompartmentItemsManager: Adding new item:', newItem)
      handleItemsChange([...items, newItem])
      resetForm()
    }

    const removeItem = (index: number) => {
      handleItemsChange(items.filter((_, idx) => idx !== index))
    }

    const updateItem = (index: number, updates: Partial<CompartmentItem>) => {
      handleItemsChange(
        items.map((item, idx) =>
          idx === index ? { ...item, ...updates } : item
        )
      )
    }

    // Debounced capacity update function to prevent premature auto-save
    const debouncedCapacityUpdate = React.useMemo(
      () =>
        debounce((index: number, capacityValue: number) => {
          console.log('🔄 Debounced capacity update:', { index, capacityValue })
          updateItem(index, { capacityValue })
        }, 500), // 500ms delay allows user to finish typing
      [debounce]
    )

    // Get icon for product based on category or name
    const getProductIcon = React.useCallback(
      (productId: string) => {
        const product = products?.find(p => p._id === productId)
        if (!product) return '📦'

        const name = product.name?.toLowerCase() || ''
        const category = product.category?.toLowerCase() || ''

        if (name.includes('fuel') || category.includes('fuel')) return '⛽'
        if (name.includes('parcel') || category.includes('parcel')) return '📦'
        if (name.includes('refrigerat') || category.includes('refrigerat'))
          return '🧊'
        if (name.includes('hazmat') || product.hazmatClass) return '⚠️'
        return '📦'
      },
      [products]
    )

    // Calculate total compartment usage
    const totalCompartmentUsage = React.useMemo(() => {
      return items.reduce(
        (total, item) => {
          const capacity = item.capacityValue
          // Convert all to base units for comparison
          let normalizedCapacity = capacity
          if (item.capacityUnit === 'tons') normalizedCapacity *= 1000
          if (item.capacityUnit === 'cubic_meters') normalizedCapacity *= 1000

          return [
            total[0] +
              (item.capacityUnit.includes('kg') ||
              item.capacityUnit.includes('tons')
                ? normalizedCapacity
                : 0),
            total[1] +
              (item.capacityUnit.includes('liters') ||
              item.capacityUnit.includes('cubic_meters')
                ? normalizedCapacity
                : 0),
            total[2] +
              (item.capacityUnit.includes('items') ||
              item.capacityUnit.includes('pieces')
                ? capacity
                : 0),
          ]
        },
        [0, 0, 0]
      )
    }, [items])

    // Early validation - ensure products are loaded
    if (!products || products.length === 0) {
      return (
        <div ref={ref} className={cn('space-y-3', className)}>
          <div className="text-sm text-destructive">
            Cannot manage compartments: Products not loaded. Please ensure
            products exist for this project.
          </div>
        </div>
      )
    }

    return (
      <div ref={ref} className={cn('space-y-3', className)}>
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Vehicle Compartments</Label>
          <div className="flex items-center gap-2">
            {/* Simplified compartments header - backend handles all calculations */}
            <Popover open={isAddingItem} onOpenChange={setIsAddingItem}>
              <PopoverTrigger asChild>
                <Button
                  disabled={disabled}
                  size="sm"
                  variant="outline"
                  className="h-8"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Compartment
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="w-96 p-4 bg-gray-100"
                data-slot="compartment-form-popover"
              >
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">
                    Add New Compartment
                  </Label>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="space-y-2">
                      <Label className="text-sm">Product</Label>
                      <select
                        className="w-full h-9 px-2 border rounded text-sm bg-background"
                        value={selectedProductId}
                        onChange={e => {
                          setSelectedProductId(e.target.value)
                          // Auto-update unit based on selected product
                          const units = getUnitsForProduct(e.target.value)
                          if (units.length > 0) {
                            setSelectedCapacityUnit(units[0])
                          }
                        }}
                        disabled={disabled}
                      >
                        <option value="">Select Product</option>
                        {availableProducts.map(product => (
                          <option key={product._id} value={product._id}>
                            {getProductIcon(product._id)} {product.name}
                            {product.category && ` (${product.category})`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-2">
                        <Label className="text-sm">Capacity</Label>
                        <Input
                          type="number"
                          placeholder="1000"
                          min="1"
                          value={selectedCapacityValue}
                          onChange={e =>
                            setSelectedCapacityValue(
                              parseInt(e.target.value) || 1000
                            )
                          }
                          className="h-9"
                          disabled={disabled}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Unit</Label>
                        <select
                          className="w-full h-9 px-2 border rounded text-sm bg-background"
                          value={selectedCapacityUnit}
                          onChange={e =>
                            setSelectedCapacityUnit(e.target.value)
                          }
                          disabled={disabled}
                        >
                          {getUnitsForProduct(selectedProductId).map(unit => (
                            <option key={unit} value={unit}>
                              {unit}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        onClick={confirmAddItem}
                        size="sm"
                        className="flex-1"
                        disabled={disabled || !isFormValid}
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Add Compartment
                      </Button>
                      <Button
                        onClick={resetForm}
                        size="sm"
                        variant="outline"
                        disabled={disabled}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Compartments list */}
        {items.length > 0 && (
          <div className="space-y-2">
            {items.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-2 p-2 border rounded-md bg-muted/30"
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">
                    {getProductIcon(item.productId)}
                  </span>
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{item.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {products?.find(p => p._id === item.productId)?.name ||
                        'Unknown Product'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={editingCapacities[index] ?? item.capacityValue}
                    onChange={e => {
                      const newValue = e.target.value
                      // Update local editing state immediately (no auto-save)
                      setEditingCapacities(prev => ({
                        ...prev,
                        [index]: newValue,
                      }))

                      // Debounce the actual update (triggers auto-save after 500ms)
                      const numValue = parseInt(newValue) || 0
                      if (numValue > 0) {
                        debouncedCapacityUpdate(index, numValue)
                      }
                    }}
                    onBlur={e => {
                      // Immediate save on blur (when user clicks away)
                      const finalValue = parseInt(e.target.value) || 1000
                      updateItem(index, { capacityValue: finalValue })
                      // Clear editing state
                      setEditingCapacities(prev => {
                        const { [index]: removed, ...rest } = prev
                        return rest
                      })
                    }}
                    min="1"
                    className="w-20 h-8"
                    disabled={disabled}
                  />
                  <span className="text-sm text-muted-foreground w-16">
                    {item.capacityUnit}
                  </span>
                </div>
                <Button
                  onClick={() => removeItem(index)}
                  disabled={disabled}
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Capacity summary */}
        {showCapacityValidation && items.length > 0 && (
          <div className="p-2 bg-muted/30 rounded text-sm">
            <div className="font-semibold text-muted-foreground mb-1">
              Total Compartment Capacity:
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <span>{totalCompartmentUsage[0]}kg weight</span>
              <span>{totalCompartmentUsage[1]}L volume</span>
              <span>{totalCompartmentUsage[2]} items</span>
            </div>
          </div>
        )}

        {/* Empty state */}
        {items.length === 0 && (
          <div className="text-center py-4 text-sm text-muted-foreground">
            No compartments defined.
            <br />
            Click "Add Compartment" to configure vehicle capacity.
          </div>
        )}
      </div>
    )
  }
)
CompartmentItemsManager.displayName = 'CompartmentItemsManager'

// =============================================================================
// PHASE 2C TASK 3: PROGRESSIVE DISCLOSURE & WORKFLOW ENHANCEMENT
// =============================================================================

// Smart UI State Management Hook for VRP Capacity System
interface UIStateConfig {
  enableAutoSave?: boolean
  autoSaveDelay?: number // milliseconds
  enableProgressiveDisclosure?: boolean
  enableMobileOptimizations?: boolean
  enableWorkflowHints?: boolean
  defaultCollapsedSections?: string[]
  persistState?: boolean // Save UI state to localStorage
}

interface UIState {
  collapsedSections: Set<string>
  isDirty: boolean
  lastSaved: number | null
  currentStep: number
  completedSteps: Set<number>
  validationErrors: Record<string, string[]>
  isLoading: boolean
  isMobile: boolean
}

const useVRPUIState = (config: UIStateConfig = {}) => {
  const {
    enableAutoSave = true,
    autoSaveDelay = 2000,
    enableProgressiveDisclosure = true,
    enableMobileOptimizations = true,
    enableWorkflowHints = true,
    defaultCollapsedSections = [],
    persistState = true,
  } = config

  // Initialize state from localStorage if enabled
  const [state, setState] = React.useState<UIState>(() => {
    const initialState: UIState = {
      collapsedSections: new Set(defaultCollapsedSections),
      isDirty: false,
      lastSaved: null,
      currentStep: 0,
      completedSteps: new Set(),
      validationErrors: {},
      isLoading: false,
      isMobile: false,
    }

    if (persistState && typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('vrp-ui-state')
        if (saved) {
          const parsed = JSON.parse(saved)
          return {
            ...initialState,
            collapsedSections: new Set(parsed.collapsedSections || []),
            currentStep: parsed.currentStep || 0,
            completedSteps: new Set(parsed.completedSteps || []),
          }
        }
      } catch (error) {
        console.warn('Failed to load UI state from localStorage:', error)
      }
    }

    return initialState
  })

  // Mobile detection
  React.useEffect(() => {
    if (!enableMobileOptimizations) return

    const checkMobile = () => {
      setState(prev => ({ ...prev, isMobile: window.innerWidth < 768 }))
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [enableMobileOptimizations])

  // Persist state to localStorage
  React.useEffect(() => {
    if (!persistState || typeof window === 'undefined') return

    const stateToSave = {
      collapsedSections: Array.from(state.collapsedSections),
      currentStep: state.currentStep,
      completedSteps: Array.from(state.completedSteps),
    }

    try {
      localStorage.setItem('vrp-ui-state', JSON.stringify(stateToSave))
    } catch (error) {
      console.warn('Failed to save UI state to localStorage:', error)
    }
  }, [
    state.collapsedSections,
    state.currentStep,
    state.completedSteps,
    persistState,
  ])

  // Auto-save functionality
  const autoSaveTimeoutRef = React.useRef<NodeJS.Timeout>()
  React.useEffect(() => {
    if (!enableAutoSave || !state.isDirty) return

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current)
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      setState(prev => ({ ...prev, isDirty: false, lastSaved: Date.now() }))
      // Trigger actual save callback here in real implementation
    }, autoSaveDelay)

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
    }
  }, [state.isDirty, enableAutoSave, autoSaveDelay])

  // State management functions
  const toggleSection = React.useCallback((sectionId: string) => {
    setState(prev => {
      const newCollapsed = new Set(prev.collapsedSections)
      if (newCollapsed.has(sectionId)) {
        newCollapsed.delete(sectionId)
      } else {
        newCollapsed.add(sectionId)
      }
      return { ...prev, collapsedSections: newCollapsed }
    })
  }, [])

  const markDirty = React.useCallback(() => {
    setState(prev => ({ ...prev, isDirty: true }))
  }, [])

  const setCurrentStep = React.useCallback((step: number) => {
    setState(prev => ({ ...prev, currentStep: step }))
  }, [])

  const completeStep = React.useCallback((step: number) => {
    setState(prev => {
      const newCompleted = new Set(prev.completedSteps)
      newCompleted.add(step)
      return { ...prev, completedSteps: newCompleted }
    })
  }, [])

  const setValidationErrors = React.useCallback(
    (errors: Record<string, string[]>) => {
      setState(prev => ({ ...prev, validationErrors: errors }))
    },
    []
  )

  const setLoading = React.useCallback((loading: boolean) => {
    setState(prev => ({ ...prev, isLoading: loading }))
  }, [])

  return {
    state,
    actions: {
      toggleSection,
      markDirty,
      setCurrentStep,
      completeStep,
      setValidationErrors,
      setLoading,
    },
    config: {
      enableProgressiveDisclosure,
      enableMobileOptimizations,
      enableWorkflowHints,
    },
  }
}

// Enhanced Workflow Stepper Component for VRP Operations
interface WorkflowStep {
  id: string
  title: string
  description: string
  icon?: string
  isRequired: boolean
  estimatedTime?: string // "5 min", "15 min", etc.
  validationRules?: string[]
}

interface WorkflowStepperProps {
  steps: WorkflowStep[]
  currentStep: number
  completedSteps: Set<number>
  onStepClick?: (stepIndex: number) => void
  validationErrors?: Record<string, string[]>
  className?: string
  enableMobileCollapse?: boolean
  showProgress?: boolean
  showEstimatedTime?: boolean
}

const WorkflowStepper = React.forwardRef<HTMLDivElement, WorkflowStepperProps>(
  (
    {
      steps,
      currentStep,
      completedSteps,
      onStepClick,
      validationErrors = {},
      className,
      enableMobileCollapse = true,
      showProgress = true,
      showEstimatedTime = true,
    },
    ref
  ) => {
    const [mobileCollapsed, setMobileCollapsed] = React.useState(true)
    const [isMobile, setIsMobile] = React.useState(false)

    React.useEffect(() => {
      const checkMobile = () => setIsMobile(window.innerWidth < 768)
      checkMobile()
      window.addEventListener('resize', checkMobile)
      return () => window.removeEventListener('resize', checkMobile)
    }, [])

    const getStepStatus = (index: number) => {
      if (completedSteps.has(index)) return 'completed'
      if (index === currentStep) return 'current'
      if (index < currentStep) return 'available'
      return 'disabled'
    }

    const getStepIcon = (step: WorkflowStep, index: number, status: string) => {
      if (status === 'completed') return '✓'
      if (step.icon) return step.icon
      return (index + 1).toString()
    }

    const totalProgress = (completedSteps.size / steps.length) * 100

    return (
      <div ref={ref} className={cn('space-y-4', className)}>
        {/* Progress bar */}
        {showProgress && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-foreground">
                Step {currentStep + 1} of {steps.length}
              </span>
              <span className="text-muted-foreground">
                {Math.round(totalProgress)}% complete
              </span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-500"
                style={{ width: `${totalProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Mobile collapse toggle */}
        {isMobile && enableMobileCollapse && (
          <Button
            variant="outline"
            onClick={() => setMobileCollapsed(!mobileCollapsed)}
            className="w-full justify-between"
          >
            <span>Workflow Steps</span>
            <span
              className={cn(
                'transition-transform',
                mobileCollapsed && 'rotate-180'
              )}
            >
              ⌄
            </span>
          </Button>
        )}

        {/* Steps list */}
        <div
          className={cn(
            'space-y-3',
            isMobile && enableMobileCollapse && mobileCollapsed && 'hidden'
          )}
        >
          {steps.map((step, index) => {
            const status = getStepStatus(index)
            const hasErrors = validationErrors[step.id]?.length > 0
            const isClickable = status !== 'disabled' && onStepClick

            return (
              <div
                key={step.id}
                className={cn(
                  'flex items-start gap-4 p-4 rounded-lg border transition-all',
                  status === 'current' && 'border-primary bg-primary/5',
                  status === 'completed' &&
                    'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/20',
                  status === 'available' &&
                    'border-muted-foreground/20 hover:border-muted-foreground/30',
                  status === 'disabled' && 'opacity-50',
                  hasErrors &&
                    'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20',
                  isClickable && 'cursor-pointer'
                )}
                onClick={() => isClickable && onStepClick(index)}
              >
                {/* Step icon */}
                <div
                  className={cn(
                    'flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold shrink-0',
                    status === 'completed' && 'bg-emerald-500 text-white',
                    status === 'current' &&
                      'bg-primary text-primary-foreground',
                    status === 'available' && 'bg-muted text-muted-foreground',
                    status === 'disabled' &&
                      'bg-muted/50 text-muted-foreground',
                    hasErrors && 'bg-red-500 text-white'
                  )}
                >
                  {hasErrors ? '!' : getStepIcon(step, index, status)}
                </div>

                {/* Step content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-foreground">
                      {step.title}
                      {step.isRequired && (
                        <span
                          className="text-red-500 ml-1"
                          title="Required step"
                        >
                          *
                        </span>
                      )}
                    </h3>
                    {showEstimatedTime && step.estimatedTime && (
                      <span className="text-sm text-muted-foreground">
                        {step.estimatedTime}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {step.description}
                  </p>

                  {/* Validation errors */}
                  {hasErrors && (
                    <div className="mt-2 space-y-1">
                      {validationErrors[step.id]?.map((error, idx) => (
                        <div
                          key={idx}
                          className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1"
                        >
                          <span>•</span>
                          {error}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Validation rules hint */}
                  {status === 'current' && step.validationRules && (
                    <div className="mt-2 p-2 bg-muted/30 rounded text-sm space-y-1">
                      <div className="font-semibold text-muted-foreground">
                        Requirements:
                      </div>
                      {step.validationRules.map((rule, idx) => (
                        <div
                          key={idx}
                          className="text-muted-foreground flex items-center gap-1"
                        >
                          <span>•</span>
                          {rule}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }
)
WorkflowStepper.displayName = 'WorkflowStepper'

// Mobile-Optimized Capacity Input for Touch Interfaces
interface MobileCapacityInputProps {
  value?: number[]
  onChange?: (value: number[]) => void
  labels?: { weight: string; volume: string; count: string }
  disabled?: boolean
  className?: string
  enableSwipeGestures?: boolean
  showQuickPresets?: boolean
  presets?: { label: string; value: number[] }[]
}

const MobileCapacityInput = React.forwardRef<
  HTMLDivElement,
  MobileCapacityInputProps
>(
  (
    {
      value = [0, 0, 0],
      onChange,
      labels = { weight: 'Weight (kg)', volume: 'Volume (m³)', count: 'Items' },
      disabled = false,
      className,
      enableSwipeGestures = true,
      showQuickPresets = true,
      presets = [
        { label: 'Small Load', value: [100, 5, 50] },
        { label: 'Medium Load', value: [500, 15, 100] },
        { label: 'Large Load', value: [1000, 30, 200] },
      ],
    },
    ref
  ) => {
    const [activeIndex, setActiveIndex] = React.useState(0)
    const [touchStart, setTouchStart] = React.useState<number | null>(null)

    const fields = [
      {
        key: 'weight',
        label: labels.weight,
        value: value[0] || 0,
        unit: 'kg',
        step: 10,
      },
      {
        key: 'volume',
        label: labels.volume,
        value: value[1] || 0,
        unit: 'm³',
        step: 1,
      },
      {
        key: 'count',
        label: labels.count,
        value: value[2] || 0,
        unit: 'items',
        step: 5,
      },
    ]

    const handleInputChange = (index: number, newValue: number) => {
      const newValues = [...value]
      newValues[index] = Math.max(0, newValue)
      onChange?.(newValues)
    }

    const handleQuickAdjust = (index: number, delta: number) => {
      const currentValue = value[index] || 0
      const step = fields[index].step
      handleInputChange(index, currentValue + delta * step)
    }

    // Swipe gesture handling
    const handleTouchStart = (e: React.TouchEvent) => {
      if (!enableSwipeGestures) return
      setTouchStart(e.touches[0].clientX)
    }

    const handleTouchEnd = (e: React.TouchEvent) => {
      if (!enableSwipeGestures || !touchStart) return

      const touchEnd = e.changedTouches[0].clientX
      const diff = touchStart - touchEnd

      if (Math.abs(diff) > 50) {
        if (diff > 0 && activeIndex < fields.length - 1) {
          setActiveIndex(activeIndex + 1)
        } else if (diff < 0 && activeIndex > 0) {
          setActiveIndex(activeIndex - 1)
        }
      }
      setTouchStart(null)
    }

    return (
      <div ref={ref} className={cn('space-y-4', className)}>
        {/* Quick presets for mobile */}
        {showQuickPresets && (
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-foreground">
              Quick Presets
            </Label>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {presets.map((preset, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  onClick={() => onChange?.(preset.value)}
                  disabled={disabled}
                  className="shrink-0"
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Mobile-optimized input cards */}
        <div
          className="space-y-3"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {fields.map((field, index) => (
            <div
              key={field.key}
              className={cn(
                'p-4 border rounded-lg transition-all',
                activeIndex === index &&
                  enableSwipeGestures &&
                  'ring-2 ring-primary ring-offset-2',
                'bg-background'
              )}
            >
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-semibold text-foreground">
                  {field.label}
                </Label>
                <div className="text-sm text-muted-foreground">
                  {field.unit}
                </div>
              </div>

              {/* Large touch-friendly input */}
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAdjust(index, -1)}
                  disabled={disabled || field.value <= 0}
                  className="w-12 h-12 rounded-full p-0 text-lg"
                >
                  −
                </Button>

                <div className="flex-1">
                  <Input
                    type="number"
                    value={field.value || ''}
                    onChange={e =>
                      handleInputChange(index, parseFloat(e.target.value) || 0)
                    }
                    disabled={disabled}
                    className="text-center text-lg h-12 font-semibold"
                    placeholder="0"
                    min="0"
                    step={field.step}
                  />
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAdjust(index, 1)}
                  disabled={disabled}
                  className="w-12 h-12 rounded-full p-0 text-lg"
                >
                  +
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Swipe indicator */}
        {enableSwipeGestures && (
          <div className="flex justify-center gap-1 py-2">
            {fields.map((_, idx) => (
              <div
                key={idx}
                className={cn(
                  'w-2 h-2 rounded-full transition-all',
                  idx === activeIndex ? 'bg-primary' : 'bg-muted-foreground/30'
                )}
              />
            ))}
          </div>
        )}
      </div>
    )
  }
)
MobileCapacityInput.displayName = 'MobileCapacityInput'

// =============================================================================
// PHASE 3A: CAPACITY ARRAY DISPLAY REVOLUTION
// =============================================================================

// Revolutionary capacity array display component - Phase 3A
export interface CapacityArrayDisplayProps {
  capacityArray: number[]
  compartments?: Compartment[]
  compact?: boolean
  showUtilization?: boolean
  usedCapacity?: number[]
  onClick?: () => void
  className?: string
}

const CapacityArrayDisplay = React.forwardRef<
  HTMLDivElement,
  CapacityArrayDisplayProps
>(
  (
    {
      capacityArray,
      compartments,
      compact = false,
      showUtilization = false,
      usedCapacity = [],
      onClick,
      className,
    },
    ref
  ) => {
    // If no compartments defined, show upgrade message
    if (!compartments || compartments.length === 0) {
      return (
        <div
          ref={ref}
          className={cn(
            'flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-muted/50 transition-colors',
            className
          )}
          onClick={onClick}
        >
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground italic text-sm">
              No compartments configured
            </span>
            <Button variant="outline" size="sm" className="h-6 px-2 text-sm">
              Setup Compartments
            </Button>
          </div>
        </div>
      )
    }

    // Group capacity by compartments with product context
    const compartmentDisplays = compartments.map(compartment => {
      // Extract capacity value from array using indices
      const totalCapacity = compartment.arrayIndices.reduce((sum, index) => {
        return sum + (capacityArray[index] || 0)
      }, 0)

      // Extract used capacity if available
      const currentUsed = compartment.arrayIndices.reduce((sum, index) => {
        return sum + (usedCapacity[index] || 0)
      }, 0)

      // Calculate utilization percentage
      const utilizationPercent =
        totalCapacity > 0 ? (currentUsed / totalCapacity) * 100 : 0

      // Get appropriate emoji for product type
      const getProductEmoji = (productType: string) => {
        const emojiMap: Record<string, string> = {
          fuel_diesel: '⛽',
          fuel_petrol: '⛽',
          fuel_gas: '🔥',
          parcel_small: '📦',
          parcel_medium: '📦',
          parcel_large: '📦',
          liquid: '🧪',
          bulk: '🏗️',
          refrigerated: '❄️',
          hazmat: '⚠️',
        }
        return emojiMap[productType] || '📦'
      }

      return {
        ...compartment,
        displayCapacity: totalCapacity,
        currentUsed,
        utilizationPercent,
        emoji: getProductEmoji(compartment.productType),
      }
    })

    // Format capacity value for display
    const formatCapacityValue = (value: number, unit: string) => {
      if (value >= 1000) {
        return `${(value / 1000).toLocaleString()}K${unit}`
      }
      return `${value.toLocaleString()}${unit}`
    }

    // Get utilization color
    const getUtilizationColor = (percent: number) => {
      if (percent >= 100) return 'text-red-600'
      if (percent >= 90) return 'text-orange-600'
      if (percent >= 75) return 'text-yellow-600'
      return 'text-green-600'
    }

    if (compact) {
      return (
        <div
          ref={ref}
          className={cn(
            'flex items-center gap-1 text-sm cursor-pointer hover:bg-muted/50 rounded-md p-1',
            className
          )}
          onClick={onClick}
          title={`Vehicle Capacity: ${compartmentDisplays.map(c => `${c.name} ${formatCapacityValue(c.displayCapacity, c.capacityUnit)}`).join(', ')}`}
        >
          <span>🚛</span>
          {compartmentDisplays.map((comp, index) => (
            <React.Fragment key={comp.id}>
              {index > 0 && <span className="text-muted-foreground">•</span>}
              <span className="font-semibold">
                {comp.name}:{' '}
                {formatCapacityValue(comp.displayCapacity, comp.capacityUnit)}
              </span>
              {showUtilization && comp.currentUsed > 0 && (
                <span
                  className={cn(
                    'text-sm',
                    getUtilizationColor(comp.utilizationPercent)
                  )}
                >
                  [{comp.utilizationPercent.toFixed(0)}%]
                </span>
              )}
            </React.Fragment>
          ))}
          <Badge variant="outline" className="text-sm ml-1">
            {compartmentDisplays.length}D
          </Badge>
        </div>
      )
    }

    // Full display mode
    return (
      <div ref={ref} className={cn('space-y-2', className)}>
        <div className="flex items-center gap-2">
          <span className="text-lg">🚛</span>
          <span className="font-semibold text-sm">Vehicle Capacity</span>
          <Badge variant="secondary" className="text-sm">
            {compartmentDisplays.length} compartments
          </Badge>
        </div>

        <div className="space-y-2">
          {compartmentDisplays.map(comp => (
            <div
              key={comp.id}
              className="flex items-center justify-between p-2 rounded-md bg-muted/30"
            >
              <div className="flex items-center gap-2">
                <span>{comp.emoji}</span>
                <div>
                  <div className="font-semibold text-sm">{comp.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {comp.productType.replace('_', ' ')}
                  </div>
                </div>
              </div>

              <div className="text-right">
                <div className="font-semibold text-sm">
                  {formatCapacityValue(comp.displayCapacity, comp.capacityUnit)}
                </div>
                {showUtilization && (
                  <div
                    className={cn(
                      'text-sm',
                      getUtilizationColor(comp.utilizationPercent)
                    )}
                  >
                    {comp.utilizationPercent.toFixed(0)}% used
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {onClick && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClick}
            className="w-full"
          >
            Edit Compartments
          </Button>
        )}
      </div>
    )
  }
)
CapacityArrayDisplay.displayName = 'CapacityArrayDisplay'

// Legacy component removed - now using product-based delivery system

// Product items display component for job delivery/pickup visualization - Phase 3A
export interface ProductItemsDisplayProps {
  items: Array<{
    productId: string
    productName?: string
    quantity: number
    unit: string
  }>
  capacityArray?: number[]
  products?: Array<{ _id: string; name: string; unitType: string }>
  compact?: boolean
  showCompatibility?: boolean
  compatibilityStatus?: 'compatible' | 'warning' | 'incompatible'
  onClick?: () => void
  className?: string
}

const ProductItemsDisplay = React.forwardRef<
  HTMLDivElement,
  ProductItemsDisplayProps
>(
  (
    {
      items = [],
      capacityArray,
      products = [],
      compact = false,
      showCompatibility = false,
      compatibilityStatus,
      onClick,
      className,
    },
    ref
  ) => {
    if (!items || items.length === 0) {
      return (
        <div
          ref={ref}
          className={cn('text-muted-foreground italic text-sm', className)}
        >
          No items
        </div>
      )
    }

    // Enrich items with product names if not provided
    const enrichedItems = items.map(item => ({
      ...item,
      productName:
        item.productName ||
        products.find(p => p._id === item.productId)?.name ||
        'Unknown Product',
    }))

    // Get compatibility icon
    const getCompatibilityIcon = () => {
      if (!showCompatibility) return null
      switch (compatibilityStatus) {
        case 'compatible':
          return '✅'
        case 'warning':
          return '⚠️'
        case 'incompatible':
          return '❌'
        default:
          return null
      }
    }

    if (compact) {
      return (
        <div
          ref={ref}
          className={cn(
            'flex items-center gap-1 text-sm cursor-pointer hover:bg-muted/50 rounded-md p-1',
            className
          )}
          onClick={onClick}
          title={`Product Items: ${enrichedItems.map(i => `${i.productName} ${i.quantity}${i.unit}`).join(', ')}`}
        >
          <span>📦</span>
          {enrichedItems.map((item, index) => (
            <React.Fragment key={`${item.productId}-${index}`}>
              {index > 0 && <span className="text-muted-foreground">•</span>}
              <span className="font-semibold">
                {item.productName}: {item.quantity.toLocaleString()}
                {item.unit}
              </span>
            </React.Fragment>
          ))}
          {getCompatibilityIcon() && (
            <span className="ml-1">{getCompatibilityIcon()}</span>
          )}
        </div>
      )
    }

    // Full display mode
    return (
      <div ref={ref} className={cn('space-y-2', className)}>
        <div className="flex items-center gap-2">
          <span className="text-lg">📦</span>
          <span className="font-semibold text-sm">Product Items</span>
          <Badge variant="secondary" className="text-sm">
            {enrichedItems.length} products
          </Badge>
          {getCompatibilityIcon() && <span>{getCompatibilityIcon()}</span>}
        </div>

        <div className="space-y-1">
          {enrichedItems.map((item, index) => (
            <div
              key={`${item.productId}-${index}`}
              className="flex items-center justify-between p-2 rounded-md bg-muted/30"
            >
              <div className="font-semibold text-sm">{item.productName}</div>
              <div className="text-sm">
                {item.quantity.toLocaleString()} {item.unit}
              </div>
            </div>
          ))}
        </div>

        {onClick && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClick}
            className="w-full"
          >
            Edit Items
          </Button>
        )}
      </div>
    )
  }
)
ProductItemsDisplay.displayName = 'ProductItemsDisplay'

export {
  Input,
  ProductCapacityInput,
  CurrencyInput,
  DurationInput,
  TimeRangeInput,
  CompartmentBuilder,
  CapacityUtilizationBar,
  DeliveryItemsManager,
  CompartmentItemsManager,
  // Phase 2C Task 3 exports
  useVRPUIState,
  WorkflowStepper,
  MobileCapacityInput,
  // Phase 3A exports
  CapacityArrayDisplay,
  ProductItemsDisplay,
  type Compartment,
  type CompartmentBuilderProps,
  type CapacityUtilizationBarProps,
  type DeliveryItem,
  type DeliveryItemsManagerProps,
  type CompartmentItem,
  type CompartmentItemsManagerProps,
  type UIStateConfig,
  type UIState,
  type WorkflowStep,
  type WorkflowStepperProps,
  type MobileCapacityInputProps,
  type CapacityArrayDisplayProps,
  type ProductItemsDisplayProps,
}

// Export new time window components
export {
  TimeDropdown,
  secondsToTimeString,
  timeStringToSeconds,
  type TimeDropdownProps,
} from './TimeDropdown'
// Note: ImprovedTimeWindowInput removed - using TimeWindowInput V2 instead

// Export PickupItemsManager from EditModal for now since it's defined there
export { PickupItemsManager } from './EditModal'
export type { PickupItem, PickupItemsManagerProps } from './EditModal'
